import React, { useEffect, useMemo, useRef, useState } from 'react';
import Card, { CardContent, CardHeader } from '../components/ui/Card';
import Button from '../components/ui/Button';
import { useToast } from '../hooks/useToast';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { FileText, Trash2, Upload, History, X } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import Select from '../components/ui/Select';

type InformeMonthly = {
  id: string;
  brand: string;
  month_start: string; // YYYY-MM-DD
  pdf_url: string | null;
};

type InformeHistoryItem = {
  id: string;
  brand: string;
  month_start: string;
  action: 'upsert' | 'delete';
  pdf_url: string | null;
  previous_pdf_url: string | null;
  changed_by_email: string | null;
  created_at: string;
};

const monthStartForYearIndex = (year: number, monthIndex0: number): string => {
  // monthIndex0: 0..11
  const m = String(monthIndex0 + 1).padStart(2, '0');
  return `${year}-${m}-01`;
};

const monthNameES = (monthIndex0: number, year: number) =>
  new Date(year, monthIndex0, 1).toLocaleDateString('es-AR', { month: 'long', year: 'numeric' });

const sanitizeStorageSegment = (v: string) =>
  (v || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

const ContenidoInformes: React.FC = () => {
  const { userRole, user } = useAuth();
  const { toast, ToastComponent } = useToast();
  const canHistory = userRole === 'Administrador' || userRole === 'Super Admin';
  const canManage = userRole === 'Contenido' || canHistory;

  const today = useMemo(() => new Date(), []);
  // Requisito: mostrar solo el año en curso
  const year = today.getFullYear();

  const [brands, setBrands] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);

  const [informeByKey, setInformeByKey] = useState<Record<string, InformeMonthly | null>>({});

  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyItems, setHistoryItems] = useState<InformeHistoryItem[]>([]);
  const [historyBrand, setHistoryBrand] = useState<string>('');
  const [historyMonth, setHistoryMonth] = useState<string>(''); // YYYY-MM-DD

  const pendingUploadRef = useRef<{ brand: string; month_start: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const months = useMemo(() => Array.from({ length: 12 }, (_, i) => i), []);
  // Requisito: para el año 2026 ocultar enero y febrero (solo para este año)
  const monthsToRender = useMemo(() => {
    if (year === 2026) return months.filter(m => m >= 2); // 02..11 => Mar..Dic
    return months;
  }, [months, year]);

  const reloadAll = async (y: number) => {
    setLoading(true);
    try {
      const [{ data: brandRows, error: brandErr }, { data: informeRows, error: informesErr }] =
        await Promise.all([
          supabase.from('content_calendars').select('brand').order('brand', { ascending: true }),
          supabase
            .from('content_informes_monthly')
            .select('id, brand, month_start, pdf_url')
            .gte('month_start', `${y}-01-01`)
            .lt('month_start', `${y + 1}-01-01`),
        ]);

      if (brandErr) throw brandErr;
      if (informesErr) throw informesErr;

      const brandsFinal = Array.from(
        new Set((brandRows || []).map((r: any) => r.brand).filter(Boolean))
      ) as string[];
      setBrands(brandsFinal);

      const distinctMap: Record<string, InformeMonthly> = {};
      for (const row of informeRows || []) {
        const r = row as InformeMonthly;
        distinctMap[`${r.brand}__${r.month_start}`] = r;
      }

      const next: Record<string, InformeMonthly | null> = {};
      for (const b of brandsFinal) {
        for (const mi of monthsToRender) {
          const ms = monthStartForYearIndex(y, mi);
          const k = `${b}__${ms}`;
          next[k] = distinctMap[k] || null;
        }
      }

      setInformeByKey(next);
    } catch (e: any) {
      console.error(e);
      toast.error('Error', 'No se pudieron cargar los informes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void reloadAll(year);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openHistory = async (brand: string, month_start: string) => {
    if (!canHistory) return;
    try {
      setHistoryBrand(brand);
      setHistoryMonth(month_start);
      const { data, error } = await supabase
        .from('content_informes_monthly_history')
        .select('id, brand, month_start, action, pdf_url, previous_pdf_url, changed_by_email, created_at')
        .eq('brand', brand)
        .eq('month_start', month_start)
        .order('created_at', { ascending: false });
      if (error) throw error;
      setHistoryItems((data || []) as InformeHistoryItem[]);
      setHistoryOpen(true);
    } catch (e: any) {
      console.error(e);
      toast.error('Error', 'No se pudo cargar el historial');
    }
  };

  const triggerUpload = (brand: string, month_start: string) => {
    pendingUploadRef.current = { brand, month_start };
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] || null;
    e.target.value = '';
    const pending = pendingUploadRef.current;
    pendingUploadRef.current = null;
    if (!f || !pending) return;

    if (f.type && !f.type.includes('pdf')) {
      toast.error('Formato inválido', 'El archivo debe ser un PDF');
      return;
    }

    if (!user?.id) {
      toast.error('Sin sesión', 'Iniciá sesión para cargar un informe');
      return;
    }

    setSaving(true);
    try {
      const bucket = 'contenido-informes';
      const brandSeg = sanitizeStorageSegment(pending.brand);
      const key = `${brandSeg}/${pending.month_start}/${user.id}-${Date.now()}-${uuidv4()}.pdf`;

      const { error: upErr } = await supabase.storage
        .from(bucket)
        .upload(key, f, { contentType: 'application/pdf', upsert: false });
      if (upErr) throw upErr;

      const { data: urlData } = supabase.storage.from(bucket).getPublicUrl(key);
      const pdf_url = (urlData as any).publicUrl as string;

      const { error: rpcErr } = await supabase.rpc('admin_upsert_content_informe_month', {
        p_brand: pending.brand,
        p_month_start: pending.month_start,
        p_pdf_url: pdf_url,
      });
      if (rpcErr) throw rpcErr;

      toast.success('Informe cargado', `${pending.brand} - ${pending.month_start}`);
      await reloadAll(year);
    } catch (err: any) {
      console.error(err);
      toast.error('Error', err?.message || 'No se pudo guardar el informe');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (brand: string, month_start: string) => {
    if (!canManage) {
      toast.error('Sin permisos', 'No podés eliminar informes');
      return;
    }

    const ok = window.confirm(`¿Eliminar el PDF del informe?\nMarca: ${brand}\nMes: ${month_start}`);
    if (!ok) return;

    setSaving(true);
    try {
      const { error: rpcErr } = await supabase.rpc('admin_delete_content_informe_month', {
        p_brand: brand,
        p_month_start: month_start,
      });
      if (rpcErr) throw rpcErr;
      toast.success('Informe eliminado');
      await reloadAll(year);
    } catch (e: any) {
      console.error(e);
      toast.error('Error', e?.message || 'No se pudo eliminar el informe');
    } finally {
      setSaving(false);
    }
  };

  const headerBrands = brands;
  const safeBrands = headerBrands;

  return (
    <div className="max-w-6xl mx-auto px-4 py-5 space-y-4">
      {ToastComponent}

      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold text-gray-900 dark:text-white mb-1">Informes mensuales</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">Carga por marca y mes. Se guarda historial para Administrador.</p>
        </div>
      </div>

      <Card>
        <CardHeader
          title={
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
              Vista Mes x Marca ({year})
            </div>
          }
        />
        <CardContent>
          {loading ? (
            <div className="text-center py-10 text-gray-500">Cargando...</div>
          ) : (
            <div className="overflow-x-auto">
              {safeBrands.length === 0 ? (
                <div className="text-sm text-gray-500 py-8">
                  No hay marcas configuradas en <span className="font-medium">content_calendars</span>.
                </div>
              ) : null}
              <table className="min-w-[900px] w-full border-collapse">
                <thead>
                  <tr>
                    <th className="sticky left-0 z-10 bg-white dark:bg-gray-900 border-b px-3 py-2 text-left text-sm font-semibold text-gray-700 dark:text-gray-200">
                      Mes
                    </th>
                    {safeBrands.map(b => (
                      <th
                        key={b}
                        className="bg-white dark:bg-gray-900 border-b px-3 py-2 text-left text-sm font-semibold text-gray-700 dark:text-gray-200"
                      >
                        {b}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {monthsToRender.map(mi => {
                    const month_start = monthStartForYearIndex(year, mi);
                    return (
                      <tr key={month_start} className="border-b last:border-b-0">
                        <td className="sticky left-0 bg-white dark:bg-gray-900 border-r px-3 py-2 text-sm font-medium text-gray-800 dark:text-gray-100">
                          {monthNameES(mi, year)}
                        </td>
                        {safeBrands.map(brand => {
                          const key = `${brand}__${month_start}`;
                          const row = informeByKey[key];
                          const hasPdf = Boolean(row?.pdf_url);
                          return (
                            <td key={key} className="border-r px-2 py-2 align-top">
                              <div className="space-y-2">
                                {hasPdf ? (
                                  <>
                                    <div className="flex items-center justify-between gap-2">
                                      <a
                                        href={row?.pdf_url || undefined}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-indigo-600 dark:text-indigo-400 text-xs font-medium hover:underline truncate"
                                        title="Abrir PDF"
                                      >
                                        PDF
                                      </a>
                                      {canHistory ? (
                                        <button
                                          className="inline-flex items-center justify-center p-1 rounded border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800"
                                          onClick={() => openHistory(brand, month_start)}
                                          title="Ver historial"
                                        >
                                          <History className="h-3.5 w-3.5" />
                                        </button>
                                      ) : null}
                                    </div>
                                    {canManage ? (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        icon={<Trash2 className="h-4 w-4" />}
                                        disabled={saving}
                                        className="w-full"
                                        onClick={() => handleDelete(brand, month_start)}
                                      >
                                        Eliminar
                                      </Button>
                                    ) : null}
                                  </>
                                ) : (
                                  <>
                                    {canManage ? (
                                      <Button
                                        size="sm"
                                        variant="secondary"
                                        icon={<Upload className="h-4 w-4" />}
                                        disabled={saving}
                                        className="w-full"
                                        onClick={() => triggerUpload(brand, month_start)}
                                      >
                                        Subir PDF
                                      </Button>
                                    ) : (
                                      <div className="text-xs text-gray-500">—</div>
                                    )}
                                  </>
                                )}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf"
            className="hidden"
            onChange={handleFileSelected}
          />
        </CardContent>
      </Card>

      {historyOpen ? (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Historial</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {historyBrand} - {historyMonth}
                </p>
              </div>
              <button
                className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-200"
                onClick={() => setHistoryOpen(false)}
                aria-label="Cerrar"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {historyItems.length ? (
              <div className="space-y-3">
                {historyItems.map((h, idx) => (
                  <div
                    key={h.id || `${h.action}-${idx}`}
                    className="p-3 rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                        {h.action === 'upsert' ? 'Subida / Reemplazo' : 'Eliminación'}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {h.created_at ? new Date(h.created_at).toLocaleString('es-AR') : ''}
                      </div>
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-300 mt-1">
                      Por: {h.changed_by_email || '—'}
                    </div>
                    {h.previous_pdf_url ? (
                      <div className="mt-2 text-xs text-gray-600 dark:text-gray-300">
                        Anterior:{' '}
                        <a href={h.previous_pdf_url} target="_blank" rel="noreferrer" className="text-indigo-600 dark:text-indigo-400 hover:underline">
                          PDF
                        </a>
                      </div>
                    ) : null}
                    {h.pdf_url ? (
                      <div className="mt-2 text-xs text-gray-600 dark:text-gray-300">
                        Actual:{' '}
                        <a href={h.pdf_url} target="_blank" rel="noreferrer" className="text-indigo-600 dark:text-indigo-400 hover:underline">
                          PDF
                        </a>
                      </div>
                    ) : (
                      <div className="mt-2 text-xs text-gray-500">No hay PDF actual</div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm text-gray-500">Sin historial.</div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default ContenidoInformes;

